import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { UnifiedChatWidget } from '@/components/chat/UnifiedChatWidget';
import { supabase } from '@/integrations/supabase/client';
import { ChatTheme } from '@/types/theme';
import { AIChatbotSettings } from '@/types/ai';

/**
 * Single source of truth for chat widget
 * Used for both preview (via iframe) and production embedding
 */
export default function EmbeddedChat() {
  const { chatbotId } = useParams<{ chatbotId: string }>();
  const [searchParams] = useSearchParams();
  const [chatbot, setChatbot] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get theme from URL param if provided (used for live preview in builder)
  const themeParam = searchParams.get('theme');
  const overrideTheme = themeParam ? JSON.parse(decodeURIComponent(themeParam)) : null;

  // Get settings from URL param if provided (used for live preview in builder)
  const settingsParam = searchParams.get('settings');
  const overrideSettings = settingsParam ? JSON.parse(decodeURIComponent(settingsParam)) : null;

  // Fetch chatbot data
  useEffect(() => {
    const fetchChatbot = async () => {
      if (!chatbotId) {
        setError('No chatbot ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch chatbot data, questions, and settings
        const { data, error } = await supabase
          .from('chatbots')
          .select(`
            id,
            name,
            welcome_message,
            completion_message,
            theme,
            questions (
              id,
              text,
              type,
              order,
              options,
              validation_rules,
              rich_content
            ),
            chatbot_settings (
              display_settings
            )
          `)
          .eq('id', chatbotId)
          .single();

        if (error) throw error;

        // Sort questions by order
        const sortedQuestions = data.questions.sort((a: any, b: any) => a.order - b.order);

        // Process display settings
        let displaySettings: AIChatbotSettings = {
          messageStyle: 'traditional',
          messageDelay: 1000,
          typingSpeed: 400,
          showTypingIndicator: true,
          streamingForAIOnly: true
        };

        if (data.chatbot_settings?.display_settings) {
          const dbSettings = data.chatbot_settings.display_settings;
          displaySettings = {
            messageStyle: dbSettings.message_style || 'traditional',
            messageDelay: dbSettings.message_delay || 1000,
            typingSpeed: dbSettings.typing_speed || 400,
            showTypingIndicator: dbSettings.show_typing_indicator !== false,
            streamingForAIOnly: dbSettings.streaming_for_ai_only !== false
          };
        }

        setChatbot({
          id: data.id,
          name: data.name,
          welcomeMessage: data.welcome_message,
          completionMessage: data.completion_message,
          theme: overrideTheme || data.theme,
          questions: sortedQuestions,
          settings: overrideSettings || displaySettings
        });

      } catch (err) {
        console.error('Error fetching chatbot:', err);
        setError('Could not load the chatbot');
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatbot();
  }, [chatbotId, overrideTheme, overrideSettings]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="chat-loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Show error state
  if (error || !chatbot) {
    return (
      <div className="chat-error-container">
        <p>{error || 'Unknown error'}</p>
      </div>
    );
  }

  // Render unified chat widget with fetched data
  return (
    <UnifiedChatWidget
      chatbotId={chatbot.id}
      theme={chatbot.theme}
      questions={chatbot.questions}
      welcomeMessage={chatbot.welcomeMessage}
      completionMessage={chatbot.completionMessage}
      operatorName={chatbot.name}
      initialOpen={true}
      title={chatbot.name}
      messageDelay={chatbot.settings.messageDelay}
      validationConfig={{
        enabled: true,
        maxAttempts: 2,
        strictValidation: false
      }}
    />
  );
}
